import React, { useEffect, useMemo, useState } from 'react';
import { useTransferLog, useAccounts, useSeats } from '../../hooks/useApi';
import { useSeatSession } from '../useSeatSession';
import { TransferLogEntry, TransferSource, TransferStatus, TransferAttempt } from '../../types';
import { trackEvent } from '../../services/analytics';
import { Select } from '../../components/base-components';
import { DataList, DataListCell, DataListRow, type DataListColumn } from '../components/DataList';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { PageShell } from '../components/PageShell';
import { SegmentedControl, type SegmentOption } from '../components/SegmentedControl';
import { SkeletonList } from '../components/Skeleton';
import { ChevronDownIcon, SearchIcon } from '../components/icons';
import { cx, FOCUS_RING } from '../components/styles';

/**
 * v2 port of `src/pages/TransfersPage.tsx` (DESIGN_PLAN §3.3).
 *
 * The logic block is copied verbatim from v1: `page`, `source`, `statusFilter`,
 * `accountId`, `expandedId`, `AttemptRow`'s `showPayload`, `useAccounts`,
 * `useTransferLog(params)`, the `params` memo, `PAGE_SIZE = 50`, the
 * reset-to-page-0 effect, `trackEvent('transfers_page_viewed')`, `formatWhen`,
 * `formatRecipient`, `formatQty` and every colour map. Only the JSX changed.
 *
 * The one real defect this fixes (§3.3.3): v1's rows are intrinsic flex, so
 * nothing lines up column-to-column and the eye cannot scan down a column. At
 * `lg`+ the rows are now a genuine aligned 12-column table via `DataList`;
 * below `lg` they stack, because v1's row was always the correct *mobile*
 * design — it was only ever wrong as a desktop layout. There is no horizontal
 * table scroll at any width; the only `overflow-x-auto` is on the payload
 * `<pre>` blocks, which v1 already had.
 *
 * §3.3.4 — surfacing problems. An audit log is consulted most when something
 * went wrong, but v1 buries failed / partial / rate-limited rows in strict
 * chronological order among the successes. Three additions, all presentational:
 *
 *  - A page-scoped status summary strip. Counts are derived from the page of
 *    data already loaded and are labelled as such on every render; the
 *    all-history total stays in the header meta where it belongs. Each chip
 *    applies the EXISTING single-value `status` filter, so no new request,
 *    parameter or capability is introduced and pagination is unaffected.
 *  - An opt-in "Problems first" toggle. Default is OFF, i.e. the untouched
 *    chronological audit trail. When on it is stated in the strip, one tap
 *    reverses it, the preference persists to `localStorage`, and because the
 *    sort is stable it only hoists problems within the page already on screen —
 *    chronological order is preserved inside each group.
 *  - A tinted ring on problem rows below `lg`, where rows are separated cards.
 *    At `lg`+ the aligned Status column already carries that signal.
 *
 * Untouched: both `useApi` hooks and the import list, the `params` memo, the
 * reset-to-page-0 effect, `PAGE_SIZE`, every colour map, and the expanded
 * attempt audit with its request/response payloads.
 *
 * The first filter is "All sources" or "Team". Team is the owner's view of
 * who on their team sent what: it narrows the log to sends made by seat
 * members and adds a per-person picker. Seat members never see it — they
 * get the whole log so nobody double-sends, but sorting teammates against
 * each other is the owner's business (the server refuses them too).
 */

const PAGE_SIZE = 50;

/** '' is the whole log; 'team' is sends made by seat members (owner only). */
type LogView = '' | 'team';

/** Row-chip wording. The server says "Admin", which reads as the user's own
 * admin; these were sent for them by Flashtickets staff. */
const sourceLabel = (source: TransferSource, fallback: string) =>
  source === 'admin' ? 'Flashtickets staff' : fallback;

const STATUS_OPTIONS: { value: TransferStatus; label: string }[] = [
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'RATE_LIMITED', label: 'Rate limited' },
];

const sourceClasses: Record<TransferSource, string> = {
  flashtools: 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  flashticker: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  admin: 'bg-violet-500/10 text-violet-300 border-violet-500/20',
  unknown: 'bg-white/5 text-white/40 border-white/10',
};

const statusClasses: Record<TransferStatus, string> = {
  COMPLETED: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  PARTIAL: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  PENDING: 'bg-white/5 text-white/40 border-white/10',
  IN_PROGRESS: 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  FAILED: 'bg-red-500/10 text-red-400 border-red-500/20',
  RATE_LIMITED: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};

/**
 * The three statuses an audit log is actually consulted for. Everything about
 * them below is presentation: no status is added, removed or relabelled, and
 * every filter these drive is the existing single-value `status` query.
 */
const PROBLEM_STATUSES: TransferStatus[] = ['FAILED', 'PARTIAL', 'RATE_LIMITED'];

const isProblemStatus = (status: TransferStatus) => PROBLEM_STATUSES.indexOf(status) !== -1;

/** Labels come from `STATUS_OPTIONS` so the summary can never drift from the filter. */
const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {} as Record<TransferStatus, string>);

/** `STATUS_OPTIONS` order, problems hoisted. `sort` is stable, so ties keep source order. */
const SUMMARY_ORDER: TransferStatus[] = STATUS_OPTIONS.map((opt) => opt.value).sort(
  (a, b) => (isProblemStatus(a) ? 0 : 1) - (isProblemStatus(b) ? 0 : 1),
);

/** Ordering is a view preference, so it outlives the session. Never the filters. */
const PROBLEMS_FIRST_KEY = 'flashtools.transfers.problemsFirst';

const readProblemsFirst = (): boolean => {
  try {
    return window.localStorage.getItem(PROBLEMS_FIRST_KEY) === '1';
  } catch {
    return false;
  }
};

const formatWhen = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatRecipient = (entry: TransferLogEntry) => {
  const r = entry.recipient;
  return r.phone || r.email || '—';
};

const formatQty = (entry: TransferLogEntry) => {
  const q = entry.quantity;
  if (q.transferred === q.requested) return String(q.requested);
  return `${q.transferred}/${q.requested}`;
};

const actionClasses: Record<string, string> = {
  INITIATED: 'bg-white/5 text-white/50 border-white/10',
  API_CALLED: 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  SUCCESS: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  FAILED: 'bg-red-500/10 text-red-400 border-red-500/20',
  MANUAL_OVERRIDE: 'bg-violet-500/10 text-violet-300 border-violet-500/20',
  RETRY_SCHEDULED: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};

/** The 12-column desktop template of DESIGN_PLAN §3.3.3. Spans total 12. */
const COLUMNS: DataListColumn[] = [
  { key: 'source', label: 'Source', span: 1 },
  { key: 'status', label: 'Status', span: 1 },
  { key: 'platform', label: 'Platform', span: 1 },
  { key: 'when', label: 'When', span: 1 },
  { key: 'event', label: 'Event', span: 4 },
  { key: 'recipient', label: 'Recipient', span: 2 },
  { key: 'qty', label: 'Qty', span: 1, align: 'right' },
  { key: 'account', label: 'Source acct', span: 1 },
];

const VIEW_SEGMENTS: SegmentOption<LogView>[] = [
  { value: '', label: 'All sources', activeClassName: 'bg-white/10 text-white' },
  { value: 'team', label: 'Team', activeClassName: 'bg-[#3b82f6]/10 text-[#93c5fd]' },
];

const STATUS_SELECT_OPTIONS = [{ value: '', label: 'All statuses' }, ...STATUS_OPTIONS];

/** Badge markup shared by the desktop cells and the mobile stack. */
const Chip: React.FC<{ className: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <span
    className={cx(
      'inline-block max-w-full truncate rounded-md border px-2 py-0.5 text-[10px] font-medium',
      className,
    )}
  >
    {children}
  </span>
);

/**
 * The status summary strip (§3.3.4).
 *
 * An audit log is consulted when something went wrong, so the strip answers
 * "did anything fail?" before the user scans a single row. Two rules govern it:
 *
 * 1. Every count is derived from the page already in memory — no extra request,
 *    no new query parameter — and every count is labelled as page-scoped. The
 *    honest all-history number stays where it already was, in the header meta.
 * 2. Each chip applies the EXISTING single-value `status` filter, so the log
 *    stays server-paginated and correct. Only problem chips carry colour; the
 *    healthy ones are muted, which is what makes a bad page visible at a glance.
 */
const StatusSummaryStrip: React.FC<{
  counts: Partial<Record<TransferStatus, number>>;
  loadedCount: number;
  total?: number;
  statusFilter: TransferStatus | '';
  onSelectStatus: (status: TransferStatus | '') => void;
  problemsFirst: boolean;
  onToggleProblemsFirst: () => void;
}> = ({
  counts,
  loadedCount,
  total,
  statusFilter,
  onSelectStatus,
  problemsFirst,
  onToggleProblemsFirst,
}) => {
  const present = SUMMARY_ORDER.filter((status) => (counts[status] || 0) > 0);
  const problems = present.filter(isProblemStatus);
  const healthy = present.filter((status) => !isProblemStatus(status));
  const problemTotal = problems.reduce((sum, status) => sum + (counts[status] || 0), 0);

  const renderChip = (status: TransferStatus) => {
    const active = statusFilter === status;
    // Exactly one border/background/text tone per branch, so there is never
    // more than one candidate class for the same property.
    const tone = active
      ? 'border-white/45 bg-white/10 text-white'
      : isProblemStatus(status)
        ? statusClasses[status]
        : 'border-white/10 bg-white/[0.04] text-white/45 hover:text-white/70';

    return (
      <button
        key={status}
        type="button"
        aria-pressed={active}
        title={
          active
            ? `Clear the ${STATUS_LABELS[status]} filter`
            : `Filter the log to ${STATUS_LABELS[status]} only`
        }
        onClick={() => onSelectStatus(active ? '' : status)}
        className={cx(
          'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition-colors',
          isProblemStatus(status) ? 'font-semibold' : 'font-medium',
          tone,
          FOCUS_RING,
        )}
      >
        <span>{STATUS_LABELS[status]}</span>
        <span className="tabular-nums opacity-75">{counts[status]}</span>
        {active && <span aria-hidden="true" className="text-white/60">×</span>}
      </button>
    );
  };

  return (
    <section
      aria-label="Status summary for the transfers loaded on this page"
      className="rounded-2xl border border-white/[0.06] bg-[#0f0f0f] px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wider text-white/35">On this page</span>

          {problems.length > 0 ? (
            problems.map(renderChip)
          ) : (
            <span className="text-xs text-white/45">
              No failed, partial or rate-limited transfers
            </span>
          )}

          {healthy.length > 0 && (
            <>
              <span aria-hidden="true" className="mx-1 h-4 w-px flex-none bg-white/10" />
              {healthy.map(renderChip)}
            </>
          )}
        </div>

        {/* Meaningless while a single status is selected, so it is not offered then. */}
        {statusFilter === '' && loadedCount > 1 && (
          <button
            type="button"
            aria-pressed={problemsFirst}
            onClick={onToggleProblemsFirst}
            title={
              problemsFirst
                ? 'Back to strict chronological order'
                : 'Pin failed, partial and rate-limited transfers to the top of this page'
            }
            className={cx(
              'inline-flex flex-none items-center rounded-lg border px-2.5 py-1 text-[11px] font-medium transition-colors',
              problemsFirst
                ? 'border-[#3b82f6]/40 bg-[#3b82f6]/15 text-[#60a5fa]'
                : 'border-white/10 bg-white/[0.04] text-white/50 hover:text-white/80',
              FOCUS_RING,
            )}
          >
            Problems first
          </button>
        )}
      </div>

      <p className="mt-2 text-[11px] leading-relaxed text-white/30">
        {problemTotal > 0
          ? `${problemTotal} of the ${loadedCount} transfer${loadedCount === 1 ? '' : 's'} loaded here need${problemTotal === 1 ? 's' : ''} attention. `
          : `Counts are for the ${loadedCount} transfer${loadedCount === 1 ? '' : 's'} loaded here. `}
        {total != null
          ? `This page only — not all ${total} in the log. `
          : 'This page only. '}
        {statusFilter !== '' &&
          'A status filter is active, so other statuses are excluded from this page. '}
        {problemsFirst &&
          statusFilter === '' &&
          'Problems are pinned to the top of this page; order stays chronological within each group.'}
      </p>
    </section>
  );
};

// One attempt = one TicketTransferAudit row. Raw request/response is hidden
// behind a toggle so the expanded row stays readable.
const AttemptRow: React.FC<{ attempt: TransferAttempt }> = ({ attempt }) => {
  const [showPayload, setShowPayload] = useState(false);
  const hasPayload = attempt.request_data != null || attempt.response_data != null;
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
      <div className="flex min-w-0 items-center gap-2 flex-wrap">
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${actionClasses[attempt.action] || 'bg-white/5 text-white/50 border-white/10'}`}>
          {attempt.action_display}
        </span>
        <span className="text-white/40">{formatWhen(attempt.created_at)}</span>
        {attempt.api_endpoint && (
          <span className="min-w-0 font-mono text-[10px] text-white/40 truncate">{attempt.api_endpoint}</span>
        )}
      </div>
      {attempt.error_message && (
        <div className="mt-1.5 text-[11px] text-red-300/90 whitespace-pre-wrap break-words">{attempt.error_message}</div>
      )}
      {hasPayload && (
        <>
          <button
            onClick={() => setShowPayload((v) => !v)}
            className={cx(
              'mt-1.5 rounded text-[10px] text-white/40 hover:text-white/70 underline underline-offset-2',
              FOCUS_RING,
            )}
          >
            {showPayload ? 'Hide payload' : 'View request / response'}
          </button>
          {showPayload && (
            <div className="mt-1.5 space-y-1.5">
              {attempt.request_data != null && (
                <div>
                  <div className="text-white/40 mb-0.5">Request</div>
                  <pre className="text-[10px] font-mono text-white/60 bg-black/30 rounded p-2 overflow-x-auto max-h-48 overflow-y-auto">{JSON.stringify(attempt.request_data, null, 2)}</pre>
                </div>
              )}
              {attempt.response_data != null && (
                <div>
                  <div className="text-white/40 mb-0.5">Response</div>
                  <pre className="text-[10px] font-mono text-white/60 bg-black/30 rounded p-2 overflow-x-auto max-h-48 overflow-y-auto">{JSON.stringify(attempt.response_data, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const TransfersScreen: React.FC = () => {
  const [page, setPage] = useState(0);
  const [view, setView] = useState<LogView>('');
  const [seatId, setSeatId] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<TransferStatus | ''>('');
  const [accountId, setAccountId] = useState<number | ''>('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // The server matches event names, so the query is debounced before it enters
  // `params` — one request per pause in typing, not one per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // View preference only — it never reaches `params`, so it cannot change what
  // is fetched or how the log is paginated.
  const [problemsFirst, setProblemsFirst] = useState<boolean>(readProblemsFirst);

  const { data: accounts = [] } = useAccounts();
  const { isSeat, isLoading: seatLoading } = useSeatSession();
  // The Team view only exists for an owner who has (or has had) a team. Wait
  // for the entitlement rather than assume: a seat member must never see it.
  const { data: seatList } = useSeats({ enabled: !seatLoading && !isSeat });
  const seats = seatList?.seats ?? [];
  const canViewTeam = !seatLoading && !isSeat && seats.length > 0;
  const teamView = view === 'team' && canViewTeam;

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  const params = useMemo(() => {
    const p: Record<string, any> = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (teamView) {
      p.sent_by = 'team';
      if (seatId !== '') p.seat_id = seatId;
    }
    if (statusFilter) p.status = statusFilter;
    if (accountId !== '') p.account_id = accountId;
    if (debouncedSearch) p.search = debouncedSearch;
    return p;
  }, [teamView, seatId, statusFilter, accountId, debouncedSearch, page]);

  const { data, isLoading, error } = useTransferLog(params);

  useEffect(() => { trackEvent('transfers_page_viewed'); }, []);
  useEffect(() => { setPage(0); }, [teamView, seatId, statusFilter, accountId, debouncedSearch]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PROBLEMS_FIRST_KEY, problemsFirst ? '1' : '0');
    } catch {
      /* private mode / storage disabled — the preference is simply not remembered. */
    }
  }, [problemsFirst]);

  const transfers = data?.transfers || [];
  const pagination = data?.pagination;

  // Page-scoped, by construction: the only input is the page already fetched.
  // Both memos read `data` rather than the `transfers` local so that v1's
  // `data?.transfers || []` line stays verbatim and the deps stay stable.
  const pageStatusCounts = useMemo(() => {
    const counts: Partial<Record<TransferStatus, number>> = {};
    (data?.transfers || []).forEach((t) => {
      counts[t.status] = (counts[t.status] || 0) + 1;
    });
    return counts;
  }, [data]);

  // Opt-in, reversible, and never applied across pages: this only reorders the
  // rows already on screen. `sort` is stable, so within "problems" and within
  // "the rest" the server's chronological order is preserved exactly.
  const orderedTransfers = useMemo(() => {
    const rows = data?.transfers || [];
    if (!problemsFirst) return rows;
    return rows
      .slice()
      .sort((a, b) => (isProblemStatus(a.status) ? 0 : 1) - (isProblemStatus(b.status) ? 0 : 1));
  }, [data, problemsFirst]);

  const seatSelectOptions = [
    { value: '', label: 'Whole team' },
    ...seats.map((seat) => ({
      value: String(seat.id),
      label: `${seat.label || seat.email}${seat.status === 'revoked' ? ' (removed)' : ''}`,
    })),
  ];

  const accountSelectOptions = [
    { value: '', label: 'All accounts' },
    ...accounts.map((a) => ({
      value: String(a.id),
      label: `${a.platform} ${a.phone || a.phone_masked || a.email}`,
    })),
  ];

  return (
    <PageShell width="wide">
      <PageHeader
        title="Transfer log"
        subtitle="Every transfer initiated by you or affecting your connected accounts. Use this to audit who got what, when."
        meta={
          pagination
            ? `${pagination.total} transfer${pagination.total === 1 ? '' : 's'}`
            : undefined
        }
      />

      <div className="space-y-6">
        {/* Search */}
        <div className="relative" data-tour="transfers-search">
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            aria-label="Search transfers by event"
            placeholder="Search by event..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cx(
              'w-full rounded-xl border border-white/[0.08] bg-white/[0.03] py-3 pl-11 pr-11 text-sm text-white',
              'placeholder-white/30 transition-colors focus:border-white/20 focus:outline-none',
              FOCUS_RING,
            )}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              title="Clear search"
              className={cx(
                'absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-white/30',
                'transition-colors hover:text-white/60',
                FOCUS_RING,
              )}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </div>

        {/* Filters */}
        <div
          data-tour="transfers-filters"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center"
        >
          {canViewTeam && (
            <div className="min-w-0 sm:col-span-2 lg:flex-none">
              <SegmentedControl<LogView>
                label="Transfer log view"
                size="sm"
                value={view}
                onChange={(next) => { setView(next); setSeatId(''); }}
                options={VIEW_SEGMENTS}
                className="flex w-full flex-wrap lg:inline-flex lg:w-auto lg:flex-nowrap"
              />
            </div>
          )}

          {teamView && (
            <div className="min-w-0 [&_select]:w-full lg:[&_select]:w-auto">
              <Select
                aria-label="Filter by team member"
                value={seatId === '' ? '' : String(seatId)}
                onChange={(e) => setSeatId(e.target.value ? Number(e.target.value) : '')}
                options={seatSelectOptions}
              />
            </div>
          )}

          <div className="min-w-0 [&_select]:w-full lg:[&_select]:w-auto">
            <Select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter((e.target.value as TransferStatus) || '')}
              options={STATUS_SELECT_OPTIONS}
            />
          </div>

          <div className="min-w-0 [&_select]:w-full lg:[&_select]:w-auto">
            <Select
              aria-label="Filter by account"
              value={accountId === '' ? '' : String(accountId)}
              onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : '')}
              options={accountSelectOptions}
            />
          </div>
        </div>

        {isLoading ? (
          <div role="status" aria-label="Loading transfers…">
            <SkeletonList count={6} as="row" />
          </div>
        ) : error ? (
          <EmptyState
            tone="error"
            title={error instanceof Error ? error.message : 'Failed to load transfers'}
          />
        ) : transfers.length === 0 ? (
          <EmptyState
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            }
            title="No transfers match your filters."
          />
        ) : (
          <>
            <StatusSummaryStrip
              counts={pageStatusCounts}
              loadedCount={transfers.length}
              total={pagination?.total}
              statusFilter={statusFilter}
              onSelectStatus={setStatusFilter}
              problemsFirst={problemsFirst}
              onToggleProblemsFirst={() => setProblemsFirst((v) => !v)}
            />

            <DataList columns={COLUMNS} trailing>
              {orderedTransfers.map((t) => {
                const expanded = expandedId === t.id;
                const sourceChip = (
                  <Chip className={sourceClasses[t.source]}>{sourceLabel(t.source, t.source_display)}</Chip>
                );
                const statusChip = <Chip className={statusClasses[t.status]}>{t.status_display}</Chip>;
                const platformChip = (
                  <Chip className="bg-white/5 text-white/50 border-white/10">{t.platform}</Chip>
                );
                // Only sends made by a team member get a name chip. An account with
                // no team members therefore looks exactly as it did before the
                // feature existed, and on an account that has them the chip is
                // the answer to the only question the log could not previously
                // answer: which of us sent this.
                const senderChip = t.initiated_by?.is_seat ? (
                  <Chip className="bg-[#3b82f6]/10 text-[#93c5fd] border-[#3b82f6]/20">
                    {t.initiated_by.label || t.initiated_by.email || 'Chair'}
                  </Chip>
                ) : null;
                const when = formatWhen(t.created_at);
                // `phone` is the whole number and is absent for a seat member,
                // who falls back to the masked form the server still sends.
                const accountLabel = t.account
                  ? t.account.phone || t.account.phone_masked || t.account.email || `#${t.account.id}`
                  : null;

                const chevron = (
                  <ChevronDownIcon
                    className={cx(
                      'h-4 w-4 flex-shrink-0 text-white/40 transition-transform',
                      expanded && 'rotate-180',
                    )}
                  />
                );

                return (
                  <DataListRow
                    key={t.id}
                    expanded={expanded}
                    onClick={() => setExpandedId(expanded ? null : t.id)}
                    trailing={chevron}
                    // Below `lg` the rows are separated cards, where a tinted edge
                    // is what makes a problem findable mid-scroll. At `lg`+ the
                    // aligned Status column already does that job, so the ring is
                    // dropped rather than doubled.
                    className={
                      isProblemStatus(t.status)
                        ? cx(
                            'ring-1 lg:ring-0',
                            t.status === 'FAILED' ? 'ring-red-500/30' : 'ring-amber-500/30',
                          )
                        : undefined
                    }
                    cells={[
                      senderChip ? (
                        <span className="flex flex-wrap items-center gap-1">
                          {sourceChip}
                          {senderChip}
                        </span>
                      ) : (
                        sourceChip
                      ),
                      statusChip,
                      platformChip,
                      <span className="block truncate text-xs text-white/40" title={when}>{when}</span>,
                      <DataListCell value={t.event.name} fallback="Unknown event" className="font-medium text-white" />,
                      <DataListCell value={formatRecipient(t)} />,
                      <span className="tabular-nums">{formatQty(t)}</span>,
                      <DataListCell value={accountLabel} />,
                    ]}
                    stacked={
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {sourceChip}
                            {statusChip}
                            {platformChip}
                            {senderChip}
                            <span className="text-xs text-white/30">{when}</span>
                          </div>
                          <h3 className="text-base font-medium text-white mt-2 truncate">
                            {t.event.name || 'Unknown event'}
                          </h3>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-white/50">
                            <span>To <span className="text-white/80">{formatRecipient(t)}</span></span>
                            <span className="text-white/20">·</span>
                            <span>{formatQty(t)} ticket{t.quantity.requested === 1 ? '' : 's'}</span>
                          </div>
                          {accountLabel && (
                            <div className="mt-0.5 text-xs text-white/40">
                              from <span className="text-white/70">{accountLabel}</span>
                            </div>
                          )}
                        </div>
                        {chevron}
                      </div>
                    }
                  >
                    {expanded && (
                      <div
                        className={cx(
                          'mt-2 space-y-3 rounded-xl border border-white/[0.06] bg-black/20 px-4 py-3 text-xs text-white/60',
                          'lg:mt-0 lg:rounded-none lg:border-x-0 lg:border-t-0 lg:border-b lg:border-white/[0.05]',
                        )}
                      >
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                          <div className="min-w-0">
                            <div className="text-white/40">Sent by</div>
                            <div className="break-words text-white/80">
                              {t.initiated_by?.label || t.initiated_by_email || '—'}
                              {t.initiated_by?.is_you && (
                                <span className="ml-1 text-white/40">(you)</span>
                              )}
                            </div>
                            {t.initiated_by?.label && t.initiated_by_email && (
                              <div className="break-words text-[11px] text-white/40">
                                {t.initiated_by_email}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-white/40">Group ID</div>
                            <div className="break-all font-mono text-white/70 text-[11px]">{t.group_id}</div>
                          </div>
                          {t.recipient.email && (
                            <div className="min-w-0">
                              <div className="text-white/40">Recipient email</div>
                              <div className="break-words text-white/80">{t.recipient.email}</div>
                            </div>
                          )}
                          {(t.recipient.first_name || t.recipient.last_name) && (
                            <div className="min-w-0">
                              <div className="text-white/40">Recipient name</div>
                              <div className="break-words text-white/80">{[t.recipient.first_name, t.recipient.last_name].filter(Boolean).join(' ')}</div>
                            </div>
                          )}
                        </div>
                        {t.ticket_ids.length > 0 && (
                          <div>
                            <div className="text-white/40 mb-1">Tickets ({t.ticket_ids.length})</div>
                            <div className="flex flex-wrap gap-1">
                              {t.ticket_ids.map((id) => (
                                <span key={id} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/60 border border-white/10">
                                  {id.length > 8 ? id.slice(-8) : id}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {t.attempts && t.attempts.length > 0 && (
                          <div>
                            <div className="text-white/40 mb-1">Attempts ({t.attempts.length})</div>
                            <div className="space-y-1.5">
                              {t.attempts.map((a) => (
                                <AttemptRow key={a.id} attempt={a} />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </DataListRow>
                );
              })}
            </DataList>
          </>
        )}

        {pagination && pagination.total > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-4 pt-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className={cx(
                'min-w-[96px] px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-white/70 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed border border-white/10',
                FOCUS_RING,
              )}
            >
              Previous
            </button>
            <span className="text-xs tabular-nums text-white/40">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, pagination.total)} of {pagination.total}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!pagination.has_more}
              className={cx(
                'min-w-[96px] px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-white/70 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed border border-white/10',
                FOCUS_RING,
              )}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </PageShell>
  );
};
